import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getFullName } from "@/lib/userUtils";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import SocialUserAvatar from "@/components/avatars/SocialUserAvatar";
import OrganizationAvatar from "@/components/avatars/OrganizationAvatar";
import MemberProfileHover from "@/components/MemberProfileHover";
import VerifiedOrgMark from "@/components/VerifiedOrgMark";
import PostInteractions from "./PostInteractions";
import ThreadedComments from "./ThreadedComments";
import CommentComposer from "./CommentComposer";
import MarkdownBody from "./MarkdownBody";
import RichMarkdownEditor, {
  isMarkdownEmpty,
  normalizeEditorMarkdown,
} from "./RichMarkdownEditor";
import EmojiInsertButton from "./EmojiInsertButton";
import axios from "axios";
import { Loader2, Pencil, Trash2, ArrowLeft, AlertTriangle } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { markdownToPlain } from "@/lib/markdownPlain";
import { collectReferencesFromMarkdown, teamReferenceRoute } from "@/lib/contentReferences";
import { formatPostDateTime } from "@/lib/formatRelativeTime";
import { eventFromDialogSafePortal } from "@/lib/emojiPickerDom";
import { cardAttributionParts } from "@/lib/cardAttribution";
import {
  formatCriticalRemaining,
  isPostCriticallyActive,
} from "@/lib/feedPosts";
import { toastError } from "@/lib/toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import PostAttachmentChips from "@/components/PostAttachmentChips";
import MediaCarousel from "@/components/MediaCarousel";
import {
  resolvePostAttachmentSrc,
  uploadCommentAttachment,
} from "@/lib/postAttachments";
import {
  resolveMediaUrl,
  resolveSocialMediaSrc,
  setSocialCommentGif,
} from "@/lib/socialMedia";
import { useConfirm } from "@/components/ConfirmDialogProvider";
import { useSocialPaths } from "@/lib/socialPaths";
import { useSocialWriteApis } from "@/lib/socialWriteApis";
import {
  locationWithFocusClearedToComment,
  parseCommentThreadSearch,
} from "@/lib/permalinks";

import { API_BASE_URL } from "@/lib/apiBase";
const isLikeInteraction = (i) =>
  i.interactionType === "like" || i.interaction_type === "like";

const isCommentInteraction = (i) =>
  i.interactionType === "comment" || i.interaction_type === "comment";

/** Post-level likes have no parent; comment likes set parent_id to the comment. */
const isPostLevelLike = (i) =>
  isLikeInteraction(i) && !(i.parentId ?? i.parent_id);

/** Find a comment node (and its replies) by id in a nested tree. */
function findCommentSubtree(comments, focusId) {
  if (focusId == null || focusId === "" || !Array.isArray(comments)) {
    return null;
  }
  const target = String(focusId);
  const walk = (nodes) => {
    for (const node of nodes) {
      const id = node?.id ?? node?.interaction_id ?? node?.interactionId;
      if (id != null && String(id) === target) return node;
      if (node?.replies?.length) {
        const found = walk(node.replies);
        if (found) return found;
      }
    }
    return null;
  };
  return walk(comments);
}

const PostThreadView = ({
  post: initialPost,
  isOpen,
  onClose,
  accessToken,
  currentUserId,
  onInteractionUpdate,
  onPostUpdate,
  orgMembersMap = {},
  orgName: orgNameProp,
  isLiked: initialIsLiked = false,
  orgId: orgIdProp,
  returnToCatchupHref = null,
  onReturnToCatchup = null,
  /** "dialog" (default) overlays parent; "page" renders inline for permalinks */
  variant = "dialog",
  /** Hide the page-variant title bar when embedded next to card details. */
  hidePageHeader = false,
  /**
   * Team member ids for intake vs created copy on kanban cards.
   * @type {Set<string>|null}
   */
  teamMemberIds = null,
  /**
   * "workplace" (default) → /posts/...
   * "community" → social visitor /c/:id → /social/posts/...
   * "org-community" → workplace Community Space → /social/posts/...;
   *   official deletes use /org/:orgId/social/posts/:postId
   */
  surface = "workplace",
  /**
   * Controlled focus comment id (CardInspect). When `undefined`, focus comes from
   * `?focus=` on the permalink URL.
   */
  focusCommentId,
  /** Controlled Continue thread — when set, skips URL ?focus= navigation. */
  onContinueThread = null,
  /** Controlled exit from focus mode (CardInspect). */
  onExitFocus = null,
}) => {
  const paths = useSocialPaths();
  const writeApis = useSocialWriteApis();
  const location = useLocation();
  const navigate = useNavigate();
  const isOrgCommunity = surface === "org-community";
  const isCommunity = surface === "community" || isOrgCommunity;
  const relativeTimestamps = true;
  const socialTombstone = isCommunity;
  const actAsOrgWrites = writeApis.isActAsOrg && isCommunity;
  const formatWhen = (value) =>
    formatPostDateTime(value, { relative: relativeTimestamps });
  const confirm = useConfirm();
  const isMobile = useIsMobile();
  const [post, setPost] = useState(initialPost);
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCommentComposer, setShowCommentComposer] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [hasLiked, setHasLiked] = useState(!!initialIsLiked);
  const [likingCommentIds, setLikingCommentIds] = useState(() => new Set());
  const [handoffHistory, setHandoffHistory] = useState([]);
  const [isEditingPost, setIsEditingPost] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  /** After leaving controlled focus, scroll/highlight this comment once. */
  const [scrollToCommentId, setScrollToCommentId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [isSavingPost, setIsSavingPost] = useState(false);
  const [criticalBusy, setCriticalBusy] = useState(false);
  const [canOrgPostCritical, setCanOrgPostCritical] = useState(false);
  const [canTeamAdminCritical, setCanTeamAdminCritical] = useState(false);
  const [postBodyExpanded, setPostBodyExpanded] = useState(false);
  const [bodyNeedsExpand, setBodyNeedsExpand] = useState(false);
  const postBodyRef = useRef(null);
  const editPostEditorRef = useRef(null);

  const postId = post?.post_id || post?.postId;
  const resolvedOrgId =
    orgIdProp ||
    post?.author_org_id ||
    post?.authorOrgId ||
    post?.author_org?.org_id ||
    post?.author_org?.orgId ||
    post?.orgId ||
    post?.org_id ||
    post?.org?.org_id ||
    post?.org?.orgId ||
    post?.team?.org_id ||
    post?.team?.orgId ||
    initialPost?.author_org_id ||
    initialPost?.authorOrgId ||
    initialPost?.author_org?.org_id ||
    initialPost?.author_org?.orgId ||
    initialPost?.orgId ||
    initialPost?.org_id ||
    initialPost?.org?.org_id ||
    initialPost?.org?.orgId ||
    initialPost?.team?.org_id ||
    initialPost?.team?.orgId ||
    null;

  useEffect(() => {
    setPostBodyExpanded(false);
  }, [postId]);

  const referenceContext = useMemo(
    () =>
      resolvedOrgId && accessToken
        ? {
            enabled: true,
            orgId: resolvedOrgId,
            accessToken,
            contextType: "post",
          }
        : null,
    [resolvedOrgId, accessToken]
  );

  useEffect(() => {
    if (isCommunity || !resolvedOrgId || !accessToken) {
      setCanOrgPostCritical(false);
      setCanTeamAdminCritical(false);
      return;
    }
    const teamId = post?.teamId ?? post?.team_id ?? null;
    let cancelled = false;
    (async () => {
      try {
        const meRes = await axios.get(`${API_BASE_URL}/org/${resolvedOrgId}/me`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (cancelled) return;
        const caps = new Set(meRes.data?.capabilities || []);
        const isOwner = Boolean(meRes.data?.is_owner);
        setCanOrgPostCritical(isOwner || caps.has("org.post"));

        if (!teamId) {
          setCanTeamAdminCritical(false);
          return;
        }
        if (isOwner) {
          setCanTeamAdminCritical(true);
          return;
        }
        const membershipsRes = await axios.get(
          `${API_BASE_URL}/team/memberships`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: { org_id: resolvedOrgId },
          }
        );
        if (cancelled) return;
        const rows = Array.isArray(membershipsRes.data)
          ? membershipsRes.data
          : [];
        const tid = Number(teamId);
        setCanTeamAdminCritical(
          rows.some(
            (t) =>
              Number(t.team_id || t.teamId) === tid && t.role === "admin"
          )
        );
      } catch {
        if (!cancelled) {
          setCanOrgPostCritical(false);
          setCanTeamAdminCritical(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isCommunity,
    resolvedOrgId,
    accessToken,
    post?.teamId,
    post?.team_id,
  ]);

  const membersById = useMemo(() => {
    const map = {};
    const addUser = (user, title) => {
      const id = user?.user_id || user?.userId;
      if (!id) return;
      map[id] = {
        ...user,
        user_id: id,
        // Community Space: never attach org membership titles.
        title: isCommunity ? null : title || user.title || orgMembersMap?.[id] || null,
      };
    };
    if (!isCommunity || !(post?.is_official || post?.isOfficial)) {
      addUser(
        post?.user || post?.author,
        isCommunity ? null : orgMembersMap?.[post?.user?.user_id]
      );
    }
    for (const i of interactions) {
      addUser(
        i.user || i.author,
        isCommunity ? null : orgMembersMap?.[i.user?.user_id || i.user_id]
      );
    }
    return map;
  }, [post, interactions, orgMembersMap, isCommunity]);

  const authOpts = () => ({
    headers: { Authorization: `Bearer ${accessToken}` },
    params: !isCommunity && resolvedOrgId ? { org_id: resolvedOrgId } : {},
  });

  const postsApiBase = isCommunity
    ? actAsOrgWrites
      ? writeApis.postsBase
      : `${API_BASE_URL}/social/posts`
    : `${API_BASE_URL}/posts`;
  /** Reads stay on personal social list endpoint (org actor has no list route). */
  const interactionsListBase = isCommunity
    ? `${API_BASE_URL}/social/posts`
    : `${API_BASE_URL}/posts`;
  const interactionsDeleteUrl = (interactionId) =>
    isCommunity
      ? actAsOrgWrites
        ? null
        : `${API_BASE_URL}/social/posts/interactions/${interactionId}`
      : `${API_BASE_URL}/posts/interactions/${interactionId}`;

  useEffect(() => {
    setPost(initialPost);
    setIsEditingPost(false);
  }, [initialPost]);

  // Calculate counts from interactions (exclude comment likes from post likeCount)
  const likeCount = interactions.filter(isPostLevelLike).length;
  const commentCount = interactions.filter(isCommentInteraction).length;

  // Fetch interactions when dialog opens (sets hasLiked from loaded data — avoid race)
  useEffect(() => {
    if (isOpen && postId && accessToken) {
      setHasLiked(!!initialIsLiked);
      setShowCommentComposer(false);
      setPostBodyExpanded(false);
      fetchInteractions();
      if (!isCommunity) fetchHandoffHistory();
      else setHandoffHistory([]);
    } else {
      setInteractions([]);
      setHandoffHistory([]);
      setError(null);
      setShowCommentComposer(false);
      setHasLiked(false);
      setLikingCommentIds(new Set());
      setPostBodyExpanded(false);
    }
    // Intentionally omit fetch helpers — refresh when thread open/identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, postId, accessToken, initialIsLiked, isCommunity]);

  useLayoutEffect(() => {
    if (!post) {
      setBodyNeedsExpand(false);
      return;
    }
    const deleted = Boolean(post.deletedAt || post.deleted_at);
    if (!isMobile || deleted || isEditingPost || postBodyExpanded) {
      setBodyNeedsExpand(false);
      return;
    }
    const el = postBodyRef.current;
    if (!el) {
      setBodyNeedsExpand(false);
      return;
    }
    setBodyNeedsExpand(el.scrollHeight > el.clientHeight + 1);
  }, [
    isMobile,
    isEditingPost,
    postBodyExpanded,
    post,
    postId,
  ]);

  const fetchHandoffHistory = async () => {
    if (isCommunity || !postId || !accessToken) return;
    const fromPost =
      post?.handoff_history || post?.handoffHistory || null;
    if (Array.isArray(fromPost) && fromPost.length > 0) {
      setHandoffHistory(fromPost);
      return;
    }
    try {
      const response = await axios.get(
        `${API_BASE_URL}/posts/${postId}/handoffs`,
        authOpts()
      );
      setHandoffHistory(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Failed to fetch handoff history:", err);
      setHandoffHistory([]);
    }
  };

  const syncHasLikedFromInteractions = (list) => {
    if (!currentUserId) {
      setHasLiked(false);
      return;
    }
    const userLike = list.find(
      (i) =>
        isPostLevelLike(i) &&
        (i.userId === currentUserId || i.user_id === currentUserId)
    );
    setHasLiked(!!userLike);
  };

  const fetchInteractions = async () => {
    if (!postId || !accessToken) return;

    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${interactionsListBase}/${postId}/interactions`,
        authOpts()
      );
      const list = Array.isArray(response.data) ? response.data : [];
      setInteractions(list);
      syncHasLikedFromInteractions(list);
    } catch (err) {
      console.error("Failed to fetch interactions:", err);
      setError("Failed to load interactions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!postId || !accessToken || isLiking) return;

    const previousLiked = hasLiked;
    const previousInteractions = interactions;
    const nextLiked = !previousLiked;

    // Optimistic: toggle heart + adjust interactions so counts update immediately
    setHasLiked(nextLiked);
    if (previousLiked) {
      setInteractions((prev) =>
        prev.filter(
          (i) =>
            !(
              isPostLevelLike(i) &&
              (i.userId === currentUserId || i.user_id === currentUserId)
            )
        )
      );
    } else {
      setInteractions((prev) => [
        ...prev,
        {
          interactionType: "like",
          userId: currentUserId,
          interactionId: `optimistic-${Date.now()}`,
          parentId: null,
        },
      ]);
    }
    setIsLiking(true);

    try {
      if (previousLiked) {
        if (actAsOrgWrites) {
          // Org actor API is like-once; no unlike. Revert optimistic toggle.
          setHasLiked(previousLiked);
          setInteractions(previousInteractions);
          return;
        }
        const likeInteraction = previousInteractions.find(
          (i) =>
            isPostLevelLike(i) &&
            (i.userId === currentUserId || i.user_id === currentUserId)
        );
        if (likeInteraction) {
          await axios.delete(
            interactionsDeleteUrl(likeInteraction.interactionId || likeInteraction.interaction_id),
            authOpts()
          );
        }
      } else {
        await axios.post(
          `${postsApiBase}/${postId}/interactions`,
          {
            interaction_type: "like",
          },
          authOpts()
        );
      }

      const updatedInteractions = await axios.get(
        `${interactionsListBase}/${postId}/interactions`,
        authOpts()
      );
      const interactionsArray = Array.isArray(updatedInteractions.data)
        ? updatedInteractions.data
        : [];
      setInteractions(interactionsArray);
      syncHasLikedFromInteractions(interactionsArray);

      if (onInteractionUpdate) {
        onInteractionUpdate(postId, {
          likeCount: interactionsArray.filter(isPostLevelLike).length,
          commentCount: interactionsArray.filter(isCommentInteraction).length,
          isLiked: nextLiked,
        });
      }
    } catch (err) {
      console.error("Failed to toggle like:", err);
      setHasLiked(previousLiked);
      setInteractions(previousInteractions);
      toastError("Failed to update like. Please try again.");
    } finally {
      setIsLiking(false);
    }
  };

  // Transform API interactions to nested comment structure for ThreadedComments
  const transformInteractionsToComments = (interactions) => {
    const comments = interactions.filter(isCommentInteraction);

    if (comments.length === 0) {
      return [];
    }

    const likesByCommentId = new Map();
    interactions.filter(isLikeInteraction).forEach((like) => {
      const parentId = like.parentId ?? like.parent_id;
      if (parentId == null) return;
      const key = Number(parentId);
      if (!likesByCommentId.has(key)) {
        likesByCommentId.set(key, []);
      }
      likesByCommentId.get(key).push(like);
    });

    const commentMap = new Map();
    comments.forEach((interaction) => {
      const user = interaction.user || {};
      const commentId = Number(
        interaction.interactionId || interaction.interaction_id
      );
      const parentId = interaction.parentId || interaction.parent_id;

      const userId = user.user_id || user.userId;
      const orgTitle = isCommunity
        ? null
        : (userId && orgMembersMap && orgMembersMap[userId]) || null;

      const socialAuthor =
        interaction.social_author || interaction.socialAuthor || null;
      const userForDisplay = isCommunity
        ? {
            user_id: userId,
            display_name:
              socialAuthor?.display_name ||
              user.display_name ||
              null,
            username: socialAuthor?.username || user.username || null,
            avatar_url:
              socialAuthor?.avatar_url ||
              user.avatar_url ||
              user.avatarUrl ||
              null,
          }
        : {
            first_name: user.first_name || user.firstName || "",
            last_name: user.last_name || user.lastName || "",
            org_email: user.org_email || "",
            ...user,
          };

      const commentLikes = likesByCommentId.get(commentId) || [];
      const userLike = currentUserId
        ? commentLikes.find(
            (i) => i.userId === currentUserId || i.user_id === currentUserId
          )
        : null;

      const socialDisplayName =
        userForDisplay.display_name ||
        socialAuthor?.display_name ||
        userForDisplay.username ||
        "Unknown";

      commentMap.set(commentId, {
        id: commentId,
        content: interaction.content,
        media: Array.isArray(interaction.media) ? interaction.media : [],
        author: {
          name: isCommunity
            ? socialDisplayName
            : getFullName(userForDisplay) || "Unknown",
          role: orgTitle,
          profilePicture: null,
          username: userForDisplay.username || null,
          display_name: userForDisplay.display_name || null,
        },
        user: userForDisplay,
        social_author: socialAuthor,
        userId: interaction.userId || interaction.user_id || userId,
        created_at: interaction.createdAt || interaction.created_at,
        editedAt: interaction.editedAt || interaction.edited_at,
        deletedAt: interaction.deletedAt || interaction.deleted_at,
        deletedBy: interaction.deletedBy || interaction.deleted_by,
        parentId: parentId ? Number(parentId) : null,
        replies: [],
        likeCount: commentLikes.length,
        isLiked: !!userLike,
        likeInteractionId:
          userLike?.interactionId || userLike?.interaction_id || null,
      });
    });

    const topLevelComments = [];

    commentMap.forEach((comment) => {
      if (!comment.parentId) {
        topLevelComments.push(comment);
      } else {
        const parent = commentMap.get(comment.parentId);
        if (parent) {
          parent.replies.push(comment);
        } else {
          topLevelComments.push(comment);
        }
      }
    });

    // Engagement (likes + replies) first, then newest
    topLevelComments.sort((a, b) => {
      const engagementA = (a.likeCount || 0) + (a.replies?.length || 0);
      const engagementB = (b.likeCount || 0) + (b.replies?.length || 0);
      if (engagementB !== engagementA) return engagementB - engagementA;
      return new Date(b.created_at) - new Date(a.created_at);
    });

    const sortReplies = (comment) => {
      if (comment.replies && comment.replies.length > 0) {
        comment.replies.sort((a, b) => {
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return dateA - dateB;
        });
        comment.replies.forEach(sortReplies);
      }
    };

    topLevelComments.forEach(sortReplies);

    return topLevelComments;
  };

  const isFocusControlled = focusCommentId !== undefined;
  const urlFocusId = parseCommentThreadSearch(location.search).focusId;
  const effectiveFocusId = isFocusControlled ? focusCommentId : urlFocusId;

  const commentTree = useMemo(
    () => transformInteractionsToComments(interactions),
    // transformInteractionsToComments closes over community/user maps — rebuild when those change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [interactions, isCommunity, orgMembersMap, currentUserId]
  );

  const focusedSubtree = useMemo(
    () => findCommentSubtree(commentTree, effectiveFocusId),
    [commentTree, effectiveFocusId]
  );

  const displayComments = focusedSubtree ? [focusedSubtree] : commentTree;
  const isFocusMode = Boolean(focusedSubtree);

  const handleExitFocus = () => {
    const restoreId =
      focusedSubtree?.id ??
      focusedSubtree?.interaction_id ??
      focusedSubtree?.interactionId ??
      effectiveFocusId;
    if (isFocusControlled) {
      if (restoreId != null) setScrollToCommentId(String(restoreId));
      onExitFocus?.();
      return;
    }
    navigate(locationWithFocusClearedToComment(location, restoreId), {
      replace: true,
    });
  };

  useEffect(() => {
    if (!scrollToCommentId) return;
    const t = window.setTimeout(() => setScrollToCommentId(null), 2500);
    return () => window.clearTimeout(t);
  }, [scrollToCommentId]);

  const notifyParentCounts = (interactionsArray, isLikedOverride) => {
    if (!onInteractionUpdate) return;
    onInteractionUpdate(postId, {
      likeCount: interactionsArray.filter(isPostLevelLike).length,
      commentCount: interactionsArray.filter(isCommentInteraction).length,
      isLiked:
        typeof isLikedOverride === "boolean" ? isLikedOverride : hasLiked,
    });
  };

  const handleCommentLike = async (commentId) => {
    const targetCommentId = Number(commentId);
    if (
      !postId ||
      !accessToken ||
      !currentUserId ||
      !targetCommentId ||
      likingCommentIds.has(targetCommentId)
    ) {
      return;
    }

    const previousInteractions = interactions;
    const existingLike = interactions.find(
      (i) =>
        isLikeInteraction(i) &&
        Number(i.parentId ?? i.parent_id) === targetCommentId &&
        (i.userId === currentUserId || i.user_id === currentUserId)
    );
    const wasLiked = !!existingLike;

    setLikingCommentIds((prev) => new Set([...prev, targetCommentId]));

    if (wasLiked) {
      setInteractions((prev) =>
        prev.filter(
          (i) =>
            (i.interactionId || i.interaction_id) !==
            (existingLike.interactionId || existingLike.interaction_id)
        )
      );
    } else {
      setInteractions((prev) => [
        ...prev,
        {
          interactionType: "like",
          userId: currentUserId,
          parentId: targetCommentId,
          interactionId: `optimistic-comment-${Date.now()}`,
        },
      ]);
    }

    try {
      if (actAsOrgWrites) {
        // Comment likes are personal-only; do not write as org or as self under act-as.
        setInteractions(previousInteractions);
        return;
      }
      if (wasLiked) {
        await axios.delete(
          interactionsDeleteUrl(
            existingLike.interactionId || existingLike.interaction_id
          ),
          authOpts()
        );
      } else {
        await axios.post(
          `${postsApiBase}/${postId}/interactions`,
          {
            interaction_type: "like",
            parent_id: targetCommentId,
          },
          authOpts()
        );
      }

      const updatedInteractions = await axios.get(
        `${interactionsListBase}/${postId}/interactions`,
        authOpts()
      );
      const interactionsArray = Array.isArray(updatedInteractions.data)
        ? updatedInteractions.data
        : [];
      setInteractions(interactionsArray);
      syncHasLikedFromInteractions(interactionsArray);
      notifyParentCounts(interactionsArray);
    } catch (err) {
      console.error("Failed to toggle comment like:", err);
      setInteractions(previousInteractions);
      toastError("Failed to update like. Please try again.");
    } finally {
      setLikingCommentIds((prev) => {
        const next = new Set(prev);
        next.delete(targetCommentId);
        return next;
      });
    }
  };

  const handleSubmitComment = async (content, meta = {}) => {
    const hasMedia = Boolean(meta.gif || meta.file);
    if ((!content.trim() && !hasMedia) || !postId || !accessToken || isSubmitting) return;
    if (actAsOrgWrites && !writeApis.allowEngage) return;

    setIsSubmitting(true);
    try {
      const body = {
        interaction_type: "comment",
        content: content.trim(),
        ...(actAsOrgWrites
          ? {}
          : {
              references:
                meta.references ||
                collectReferencesFromMarkdown(content, resolvedOrgId),
            }),
        has_media: hasMedia,
      };
      // Org comments accept a Giphy gif inline at creation time.
      if (!isCommunity && meta.gif) body.gif = meta.gif;

      const { data: created } = await axios.post(
        `${postsApiBase}/${postId}/interactions`,
        body,
        authOpts()
      );

      const newCommentId = isCommunity
        ? null
        : created?.interactionId ?? created?.interaction_id;

      if (actAsOrgWrites) {
        // Org-actor comments don't support media yet — skip upload attempts.
      } else if (meta.file && !isCommunity) {
        try {
          if (newCommentId != null) {
            await uploadCommentAttachment({
              postId,
              interactionId: newCommentId,
              orgId: resolvedOrgId,
              file: meta.file,
              accessToken,
            });
          }
        } catch (mediaErr) {
          toastError(mediaErr, "Comment posted, but image upload failed");
        }
      } else if (meta.gif && isCommunity) {
        try {
          const list = Array.isArray(created) ? created : [];
          const mine = list
            .filter(isCommentInteraction)
            .filter((i) => (i.userId ?? i.user_id) === currentUserId)
            .sort(
              (a, b) => new Date(b.createdAt || b.created_at) - new Date(a.createdAt || a.created_at)
            )[0];
          const mineId = mine?.interactionId ?? mine?.interaction_id;
          if (mineId != null) {
            await setSocialCommentGif(accessToken, mineId, meta.gif);
          }
        } catch (mediaErr) {
          toastError(mediaErr, "Comment posted, but GIF attach failed");
        }
      }

      // Refresh interactions to get updated list
      const updatedInteractions = await axios.get(
        `${interactionsListBase}/${postId}/interactions`,
        authOpts()
      );
      const interactionsArray = Array.isArray(updatedInteractions.data)
        ? updatedInteractions.data
        : [];
      setInteractions(interactionsArray);

      // Notify parent of interaction update
      if (onInteractionUpdate) {
        notifyParentCounts(interactionsArray);
      }

      // Close comment composer
      setShowCommentComposer(false);
    } catch (err) {
      console.error("Failed to post comment:", err);
      toastError("Failed to post comment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReply = async (commentId, replyContent, meta = {}) => {
    const hasMedia = Boolean(meta.gif);
    if ((!replyContent.trim() && !hasMedia) || !postId || !accessToken) return;
    if (actAsOrgWrites) {
      toastError("Replies as the organization are not supported yet.");
      throw new Error("act-as-org replies unsupported");
    }

    try {
      const body = {
        interaction_type: "comment",
        content: replyContent.trim(),
        parent_id: commentId,
        references:
          meta.references ||
          collectReferencesFromMarkdown(replyContent, resolvedOrgId),
        has_media: hasMedia,
      };
      if (!isCommunity && meta.gif) body.gif = meta.gif;

      const { data: created } = await axios.post(
        `${postsApiBase}/${postId}/interactions`,
        body,
        authOpts()
      );

      if (meta.gif && isCommunity) {
        try {
          const list = Array.isArray(created) ? created : [];
          const mine = list
            .filter(isCommentInteraction)
            .filter((i) => (i.userId ?? i.user_id) === currentUserId)
            .sort(
              (a, b) =>
                new Date(b.createdAt || b.created_at) -
                new Date(a.createdAt || a.created_at)
            )[0];
          const mineId = mine?.interactionId ?? mine?.interaction_id;
          if (mineId != null) {
            await setSocialCommentGif(accessToken, mineId, meta.gif);
          }
        } catch (gifErr) {
          console.error("Failed to attach reply gif:", gifErr);
          toastError("Reply posted, but the GIF could not be attached.");
        }
      }

      // Refresh interactions to get updated list
      const updatedInteractions = await axios.get(
        `${interactionsListBase}/${postId}/interactions`,
        authOpts()
      );
      const interactionsArray = Array.isArray(updatedInteractions.data)
        ? updatedInteractions.data
        : [];
      setInteractions(interactionsArray);

      // Notify parent of interaction update
      if (onInteractionUpdate) {
        notifyParentCounts(interactionsArray);
      }
    } catch (err) {
      console.error("Failed to post reply:", err);
      toastError("Failed to post reply. Please try again.");
      throw err;
    }
  };

  const refreshInteractions = async () => {
    const updatedInteractions = await axios.get(
      `${interactionsListBase}/${postId}/interactions`,
      authOpts()
    );
    const interactionsArray = Array.isArray(updatedInteractions.data)
      ? updatedInteractions.data
      : [];
    setInteractions(interactionsArray);
    syncHasLikedFromInteractions(interactionsArray);
    notifyParentCounts(interactionsArray);
    return interactionsArray;
  };

  const handleEditComment = async (commentId, content, meta = {}) => {
    if (!accessToken || isCommunity) return;
    try {
      await axios.patch(
        interactionsDeleteUrl(commentId),
        {
          content,
          references:
            meta.references ||
            collectReferencesFromMarkdown(content, resolvedOrgId),
        },
        authOpts()
      );
      await refreshInteractions();
    } catch (err) {
      console.error("Failed to edit comment:", err);
      toastError("Failed to edit comment. Please try again.");
      throw err;
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!accessToken) return;
    try {
      await axios.delete(
        interactionsDeleteUrl(commentId),
        authOpts()
      );
      await refreshInteractions();
    } catch (err) {
      console.error("Failed to delete comment:", err);
      toastError("Failed to delete comment. Please try again.");
      throw err;
    }
  };

  const handleStartEditPost = () => {
    setEditTitle(post.title || "");
    setEditContent(post.content || "");
    setIsEditingPost(true);
  };

  const handleSavePost = async () => {
    if (!postId || !accessToken || isSavingPost) return;
    const content = normalizeEditorMarkdown(editContent);
    if (!editTitle.trim() || isMarkdownEmpty(content)) return;
    setIsSavingPost(true);
    try {
      const response = await axios.patch(
        `${postsApiBase}/${postId}`,
        {
          title: editTitle.trim(),
          content,
          references: collectReferencesFromMarkdown(content, resolvedOrgId),
        },
        authOpts()
      );
      const updated = response.data;
      setPost(updated);
      setIsEditingPost(false);
      if (onPostUpdate) {
        onPostUpdate(updated);
      }
    } catch (err) {
      console.error("Failed to update post:", err);
      toastError("Failed to update post. Please try again.");
    } finally {
      setIsSavingPost(false);
    }
  };

  const handleDeletePost = async () => {
    if (!postId || !accessToken) return;
    const ok = await confirm({
      title: "Delete post?",
      description: isCommunity
        ? "Delete this post? If it has no comments it will be removed immediately; otherwise it stays as a tombstone so the thread remains."
        : "Delete this post? The thread will remain as a tombstone and comments will be kept.",
      confirmLabel: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      let data;
      if (isOrgCommunity && (post.is_official || post.isOfficial || post.author_org_id)) {
        if (!resolvedOrgId) {
          toastError("Missing organization for official post delete");
          return;
        }
        const refreshed = await axios.delete(
          `${API_BASE_URL}/org/${encodeURIComponent(resolvedOrgId)}/social/posts/${postId}`,
          authOpts()
        );
        data = refreshed.data;
      } else if (isCommunity) {
        // Member posts (including mod-remove) always use personal social delete.
        const refreshed = await axios.delete(
          `${API_BASE_URL}/social/posts/${postId}`,
          authOpts()
        );
        data = refreshed.data;
      } else {
        await axios.delete(`${postsApiBase}/${postId}`, authOpts());
        const refreshed = await axios.get(
          `${postsApiBase}/${postId}`,
          authOpts()
        );
        data = refreshed.data;
      }

      if (data?.purged) {
        if (onPostUpdate) onPostUpdate(data);
        onClose?.();
        return;
      }
      setPost(data);
      if (onPostUpdate) onPostUpdate(data);
    } catch (err) {
      console.error("Failed to delete post:", err);
      toastError(err, "Failed to delete post. Please try again.");
    }
  };

  const handleToggleCritical = async () => {
    if (!postId || !accessToken || !resolvedOrgId || criticalBusy) return;
    const active = isPostCriticallyActive(post);
    setCriticalBusy(true);
    try {
      const { data } = active
        ? await axios.delete(`${API_BASE_URL}/posts/${postId}/critical`, {
            ...authOpts(),
          })
        : await axios.post(
            `${API_BASE_URL}/posts/${postId}/critical`,
            {},
            authOpts()
          );
      setPost(data);
      if (onPostUpdate) onPostUpdate(data);
    } catch (err) {
      console.error("Failed to update critical status:", err);
      toastError(err, "Failed to update critical status. Please try again.");
    } finally {
      setCriticalBusy(false);
    }
  };

  if (!post) return null;

  // Page variant: parent route owns visibility; dialog needs isOpen
  if (variant !== "page" && !isOpen) return null;

  const postAttachments = Array.isArray(post.attachments) ? post.attachments : [];
  const isSocialMediaPost = Array.isArray(post.media);
  const postMediaAttachments = postAttachments.filter((a) => a.is_image || a.is_video);
  const postChipAttachments = postAttachments.filter((a) => !a.is_image && !a.is_video);
  const postMediaItems = isSocialMediaPost
    ? post.media
        .filter(Boolean)
        .map((m) =>
          m.type === "video_embed" ? m : { ...m, url: resolveMediaUrl(m.url) }
        )
    : [
        ...postMediaAttachments.map((a) => ({
          type: a.is_video ? "video" : "image",
          attachmentId: a.attachment_id ?? a.attachmentId,
          alt: a.file_name || a.fileName,
        })),
        ...(post.embed ? [post.embed] : []),
      ];
  const resolvePostMediaSrc = (item) =>
    resolvePostAttachmentSrc({
      postId,
      attachmentId: item.attachmentId,
      orgId: resolvedOrgId,
      accessToken,
    });
  const resolveSocialSrc = accessToken
    ? resolveSocialMediaSrc(accessToken)
    : undefined;

  const postUserId = post.userId || post.user_id || post.user?.user_id;
  const isKanbanCard = Boolean(post.kanbanStatus || post.kanban_status);
  const cardAttribution = isKanbanCard
    ? cardAttributionParts(post, { teamMemberIds })
    : null;
  const isPostDeleted = Boolean(post.deletedAt || post.deleted_at);
  const postEditedAt = post.editedAt || post.edited_at;
  const deletedBy = post.deletedBy || post.deleted_by;
  const authorId = post.user?.user_id || post.user?.userId || postUserId;
  const isOfficial = Boolean(post.is_official || post.isOfficial);
  const authorOrg = post.author_org || post.authorOrg || null;
  const deletedByName = isOfficial
    ? authorOrg?.name || "organization"
    : getFullName(deletedBy) || "Unknown";
  const socialAuthor = post.social_author || post.socialAuthor || null;
  const authorHandle =
    socialAuthor?.username || post.user?.username || null;
  const orgSocialHandle = authorOrg?.username || null;
  const authorDisplayName =
    isOfficial && (authorOrg?.name || authorOrg?.display_name)
      ? authorOrg.name || authorOrg.display_name
      : socialAuthor?.display_name ||
        post.user?.display_name ||
        authorHandle ||
        (isCommunity ? "Unknown" : getFullName(post.user) || "Unknown");
  const orgProfileHref = orgSocialHandle
    ? paths.org(orgSocialHandle)
    : null;
  const authorRole = isCommunity
    ? null
    : (authorId && orgMembersMap?.[authorId]) ||
      post.user?.title ||
      post.user?.org_role ||
      null;
  const postTeamName =
    post.team?.name || post.teamName || post.team_name || null;
  const postTeamId =
    post.team?.team_id ??
    post.team?.teamId ??
    post.teamId ??
    post.team_id ??
    null;
  const postTeamHref =
    resolvedOrgId && postTeamId
      ? teamReferenceRoute(resolvedOrgId, postTeamId)
      : null;
  const authorMember = post.user
    ? {
        ...post.user,
        user_id: authorId || post.user.user_id,
        title: authorRole || post.user.title || null,
        org_email: post.user.org_email ?? null,
        member_avatar_url:
          post.user.member_avatar_url || post.user.avatar_url || null,
      }
    : authorId
      ? {
          user_id: authorId,
          first_name: "",
          last_name: "",
          title: authorRole,
        }
      : null;
  const postOrgName =
    orgNameProp ||
    post.org?.name ||
    post.team?.organization?.name ||
    post.team?.org?.name ||
    null;
  const canEditPost =
    !isCommunity &&
    currentUserId &&
    postUserId === currentUserId &&
    !isPostDeleted &&
    !isKanbanCard;
  const canDeletePost = isCommunity
    ? Boolean(post.can_delete || post.canDelete) && !isPostDeleted
    : !isPostDeleted &&
      !isKanbanCard &&
      Boolean(currentUserId) &&
      (postUserId === currentUserId ||
        Boolean(post.can_delete || post.canDelete));
  const isCritical = isPostCriticallyActive(post);
  const criticalUntil = post.critical_until || post.criticalUntil;
  const serverCanMark =
    typeof post.can_mark_critical === "boolean"
      ? post.can_mark_critical
      : typeof post.canMarkCritical === "boolean"
        ? post.canMarkCritical
        : null;
  // Prefer server flag (permalink). Else org owner / org.post / team admin.
  const canManageCritical =
    !isCommunity &&
    Boolean(currentUserId) &&
    !isPostDeleted &&
    !isKanbanCard &&
    (serverCanMark === true ||
      (serverCanMark !== false &&
        (canOrgPostCritical || canTeamAdminCritical)));

  const threadTitle =
    initialPost?.kanbanStatus ||
    initialPost?.kanban_status ||
    isKanbanCard
      ? "Card Thread"
      : "Post Thread";

  const compactMobilePost =
    isMobile && !isEditingPost && !postBodyExpanded;

  const showThreadTitle = !isMobile && !hidePageHeader;

  const threadBody = (
        <div
          className={cn(
            "flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden px-1 py-3 sm:px-2 md:px-4 space-y-4"
          )}
        >
          {/* Original Post */}
          <article className="min-w-0 overflow-hidden border-b border-border/60 pb-4">
            <div className="px-1 pt-1">
              <div className="flex items-start gap-3">
                {isCommunity ? null : (
                  <MemberProfileHover
                    user={authorMember}
                    orgId={resolvedOrgId}
                  >
                    <OrgMemberAvatar user={post.user || authorMember} />
                  </MemberProfileHover>
                )}
                <div className="flex-1 min-w-0">
                  {isCommunity ? (
                    <div className="mb-3 flex items-center gap-2.5 flex-wrap">
                      {isOfficial ? (
                        <OrganizationAvatar
                          org={authorOrg}
                          className="h-10 w-10"
                          fallbackClassName="text-sm"
                        />
                      ) : (
                        <SocialUserAvatar
                          user={post.user || authorMember}
                          avatarUrl={
                            post.social_author?.avatar_url ||
                            post.socialAuthor?.avatar_url ||
                            post.user?.avatar_url
                          }
                          displayName={authorDisplayName}
                          className="h-10 w-10"
                          fallbackClassName="text-sm"
                          to={
                            authorHandle ? paths.user(authorHandle) : null
                          }
                        />
                      )}
                      <div className="min-w-0 flex-1 leading-tight">
                        <div className="flex min-w-0 items-center gap-x-2">
                          {isOfficial && orgProfileHref ? (
                            <Link
                              to={orgProfileHref}
                              className="min-w-0 truncate text-base font-semibold text-foreground hover:underline"
                              onClick={(e) => e.stopPropagation()}
                              title={authorDisplayName}
                            >
                              {authorDisplayName}
                            </Link>
                          ) : authorHandle && !isOfficial ? (
                            <Link
                              to={paths.user(authorHandle)}
                              className="min-w-0 truncate text-base font-semibold text-foreground hover:underline"
                              onClick={(e) => e.stopPropagation()}
                              title={authorDisplayName}
                            >
                              {authorDisplayName}
                            </Link>
                          ) : (
                            <span
                              className="min-w-0 truncate text-base font-semibold text-foreground"
                              title={authorDisplayName}
                            >
                              {authorDisplayName}
                            </span>
                          )}
                          {isOfficial ? (
                            <VerifiedOrgMark org={authorOrg} />
                          ) : null}
                        </div>
                        {isOfficial && orgSocialHandle ? (
                          <Link
                            to={orgProfileHref}
                            className="text-xs text-muted-foreground hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            @{orgSocialHandle}
                          </Link>
                        ) : !isOfficial && authorHandle ? (
                          <Link
                            to={paths.user(authorHandle)}
                            className="text-xs text-muted-foreground hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            @{authorHandle}
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                  {isPostDeleted ? (
                    <h2 className="text-lg mb-2 italic text-muted-foreground font-semibold">
                      {socialTombstone
                        ? post.removed_by_moderator
                          ? "Removed by a moderator"
                          : "This post was deleted"
                        : `Deleted by ${deletedByName}`}
                    </h2>
                  ) : isEditingPost ? (
                    <input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      disabled={isSavingPost}
                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-lg font-semibold mb-2"
                      placeholder={isCommunity ? "Optional title" : "Title"}
                    />
                  ) : post.title ? (
                    <h2 className="text-lg mb-2 font-semibold">
                      {markdownToPlain(post.title) || post.title}
                    </h2>
                  ) : null}
                  {isCritical && (
                    <Badge
                      variant="destructive"
                      className="mb-2 gap-1 bg-amber-600 hover:bg-amber-600"
                    >
                      <AlertTriangle className="h-3 w-3" />
                      Critical
                      {formatCriticalRemaining(criticalUntil)
                        ? ` · ${formatCriticalRemaining(criticalUntil)}`
                        : ""}
                    </Badge>
                  )}
                  {isCommunity ? (
                    <div className="text-sm text-muted-foreground mb-3">
                      <span>
                        {formatWhen(post.created_at || post.createdAt)}
                        {postEditedAt && !isPostDeleted && (
                          <span className="italic"> · edited</span>
                        )}
                      </span>
                    </div>
                  ) : isKanbanCard && cardAttribution ? (
                  <div className="text-sm text-muted-foreground mb-3">
                    <div className="flex flex-wrap items-center gap-x-1.5">
                      <MemberProfileHover
                        user={authorMember}
                        orgId={resolvedOrgId}
                      >
                        <span className="font-medium text-foreground hover:underline">
                          {cardAttribution.authorName}
                        </span>
                      </MemberProfileHover>
                      <span>{cardAttribution.verb}</span>
                      {cardAttribution.teamName ? (
                        <>
                          <span>on</span>
                          {postTeamHref ? (
                            <Link
                              to={postTeamHref}
                              className="font-medium text-foreground hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {cardAttribution.teamName}
                            </Link>
                          ) : (
                            <span className="font-medium text-foreground">
                              {cardAttribution.teamName}
                            </span>
                          )}
                        </>
                      ) : null}
                      {cardAttribution.assigned ? (
                        <>
                          <span>•</span>
                          <span>
                            Assigned to{" "}
                            <span className="font-medium text-foreground">
                              {cardAttribution.assigned}
                            </span>
                          </span>
                        </>
                      ) : null}
                    </div>
                    <span className="mt-1 block">
                      {formatWhen(post.created_at || post.createdAt)}
                      {postEditedAt && !isPostDeleted && (
                        <span className="italic"> · edited</span>
                      )}
                    </span>
                  </div>
                  ) : (
                  <div className="text-sm text-muted-foreground mb-3">
                    <div className="flex flex-wrap items-center gap-x-1.5">
                      <MemberProfileHover
                        user={authorMember}
                        orgId={resolvedOrgId}
                      >
                        <span className="font-medium text-foreground hover:underline">
                          {getFullName(post.user) || "Unknown"}
                        </span>
                      </MemberProfileHover>
                      {authorRole && <span>{authorRole}</span>}
                      {postTeamName &&
                        (postTeamHref ? (
                          <Link
                            to={postTeamHref}
                            className="font-medium text-foreground hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {postTeamName}
                          </Link>
                        ) : (
                          <span className="font-medium text-foreground">
                            {postTeamName}
                          </span>
                        ))}
                      {postOrgName && (
                        <>
                          <span>@</span>
                          <span>{postOrgName}</span>
                        </>
                      )}
                    </div>
                    <span className="mt-1 block">
                      {formatWhen(post.created_at || post.createdAt)}
                      {postEditedAt && !isPostDeleted && (
                        <span className="italic"> · edited</span>
                      )}
                    </span>
                  </div>
                  )}
                </div>
              </div>
              {(canEditPost || canDeletePost || canManageCritical) &&
                !isEditingPost && (
                <div className="mt-2 flex flex-wrap items-center gap-1">
                  {canManageCritical && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleToggleCritical}
                      disabled={criticalBusy}
                      className="h-8 text-xs"
                    >
                      <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                      {criticalBusy
                        ? "…"
                        : isCritical
                          ? "Remove critical"
                          : "Mark critical"}
                    </Button>
                  )}
                  {canEditPost && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleStartEditPost}
                      className="h-8 text-xs"
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1" />
                      Edit
                    </Button>
                  )}
                  {canDeletePost && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDeletePost}
                      className="h-8 text-xs text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" />
                      Delete
                    </Button>
                  )}
                </div>
              )}
            </div>
            <div className="px-1 pt-2">
              {isPostDeleted ? (
                <p className="text-muted-foreground italic mb-4">
                  {socialTombstone
                    ? post.removed_by_moderator
                      ? "Removed by a moderator"
                      : "This post was deleted"
                    : "This post was deleted. Comments below are still available."}
                </p>
              ) : isEditingPost ? (
                <div className="space-y-3 mb-4">
                  <RichMarkdownEditor
                    ref={editPostEditorRef}
                    value={editContent}
                    onChange={setEditContent}
                    onSubmit={handleSavePost}
                    submitMode="mod-enter"
                    disabled={isSavingPost}
                    minRows={6}
                    placeholder="Edit your post…"
                    referenceContext={referenceContext}
                  />
                  <div className="flex items-center justify-between gap-2">
                    <EmojiInsertButton
                      onInsert={(native) =>
                        editPostEditorRef.current?.insertText(native)
                      }
                      disabled={isSavingPost}
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsEditingPost(false)}
                        disabled={isSavingPost}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSavePost}
                        disabled={
                          isSavingPost ||
                          !editTitle.trim() ||
                          isMarkdownEmpty(editContent)
                        }
                      >
                        {isSavingPost ? "Saving..." : "Save"}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mb-3 min-w-0 space-y-2">
                  <div
                    ref={postBodyRef}
                    className={cn(
                      "min-w-0 break-words",
                      compactMobilePost && "line-clamp-3"
                    )}
                  >
                    <MarkdownBody
                      content={post.content}
                      membersById={membersById}
                    />
                  </div>
                  {isMobile &&
                  !isPostDeleted &&
                  (bodyNeedsExpand || postBodyExpanded) ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-0 text-xs"
                      onClick={() => setPostBodyExpanded((v) => !v)}
                    >
                      {postBodyExpanded ? "Show less" : "Show more"}
                    </Button>
                  ) : null}
                </div>
              )}

              {!compactMobilePost && !isPostDeleted && postMediaItems.length > 0 ? (
                <MediaCarousel
                  className="mb-4 max-w-xl"
                  items={postMediaItems}
                  resolveSrc={
                    isSocialMediaPost ? resolveSocialSrc : resolvePostMediaSrc
                  }
                />
              ) : null}

              {!compactMobilePost &&
              !isPostDeleted &&
              postChipAttachments.length > 0 &&
              resolvedOrgId &&
              accessToken ? (
                <PostAttachmentChips
                  className="mb-4"
                  attachments={postChipAttachments}
                  postId={postId}
                  orgId={resolvedOrgId}
                  accessToken={accessToken}
                  stopPropagation={false}
                />
              ) : null}

              <div className="flex flex-wrap gap-2 mb-4">
                {(post.kanbanStatus || post.kanban_status) && (
                  <Badge variant="secondary">
                    {post.kanbanStatus || post.kanban_status}
                  </Badge>
                )}
                {post.tags &&
                  post.tags.split(",").map((tag, idx) => (
                    <Badge key={idx} variant="secondary">
                      {tag.trim()}
                    </Badge>
                  ))}
              </div>

              {!compactMobilePost && handoffHistory.length > 0 && (
                <div className="rounded-md border border-border bg-muted/30 p-3 space-y-2 mb-4">
                  <p className="text-sm font-medium">Handoff history</p>
                  <ul className="space-y-1.5">
                    {handoffHistory.map((h) => {
                      const hid = h.handoff_id ?? h.handoffId;
                      const fromName =
                        h.fromTeam?.name ||
                        h.from_team?.name ||
                        `Team ${h.fromTeamId ?? h.from_team_id}`;
                      const toName =
                        h.toTeam?.name ||
                        h.to_team?.name ||
                        `Team ${h.toTeamId ?? h.to_team_id}`;
                      const when = h.decidedAt || h.decided_at || h.createdAt || h.created_at;
                      const decider =
                        getFullName(h.decider) ||
                        getFullName(h.decidedByUser) ||
                        null;
                      return (
                        <li
                          key={hid}
                          className="text-xs text-muted-foreground flex flex-wrap items-center gap-1.5"
                        >
                          <Badge variant="outline" className="text-[10px] py-0">
                            {h.status}
                          </Badge>
                          <span>
                            {fromName} → {toName}
                          </span>
                          {when && (
                            <span>· {new Date(when).toLocaleDateString()}</span>
                          )}
                          {h.status === "accepted" && decider && (
                            <span>· accepted by {decider}</span>
                          )}
                          {h.note && (
                            <span className="w-full text-muted-foreground/80">
                              “{h.note}”
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <PostInteractions
                postId={postId}
                likeCount={likeCount}
                commentCount={commentCount}
                repostCount={post?.repost_count ?? post?.repostCount ?? 0}
                onLike={handleLike}
                onComment={() => {
                  setShowCommentComposer(!showCommentComposer);
                }}
                accessToken={accessToken}
                variant="full"
                isLiked={hasLiked}
                viewerHasReposted={Boolean(post?.viewer_has_reposted)}
                post={post}
                orgId={isCommunity ? null : resolvedOrgId}
                socialRepost={isCommunity}
                onQuoteCreated={() => {
                  /* stay on thread; quote appears in feeds */
                }}
              />

              {showCommentComposer ? (
                <div className="mt-3">
                  <CommentComposer
                    onSubmit={handleSubmitComment}
                    onCancel={() => setShowCommentComposer(false)}
                    isSubmitting={isSubmitting}
                    orgId={isCommunity ? null : resolvedOrgId}
                    accessToken={accessToken}
                    social={isCommunity}
                    className="border-0 pt-0"
                  />
                </div>
              ) : null}
            </div>
          </article>

          <Separator className="opacity-60" />

          {/* Comments Section */}
          <div>
            <div className="mb-3 flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-muted-foreground tracking-wide uppercase">
                {isFocusMode ? "Thread" : "Replies"}
              </h3>
              {isFocusMode ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 shrink-0 text-muted-foreground"
                  onClick={handleExitFocus}
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back to thread
                </Button>
              ) : null}
            </div>

            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">
                  Loading comments...
                </span>
              </div>
            ) : (
              <ThreadedComments
                comments={displayComments}
                onReply={handleReply}
                onLike={handleCommentLike}
                onEdit={isCommunity ? undefined : handleEditComment}
                onDelete={handleDeleteComment}
                currentUserId={currentUserId}
                likingCommentIds={likingCommentIds}
                orgId={isCommunity ? null : resolvedOrgId}
                accessToken={accessToken}
                membersById={membersById}
                enableMemberProfiles={!isCommunity}
                relativeTimestamps={relativeTimestamps}
                socialTombstone={socialTombstone}
                postId={post?.post_id ?? postId}
                onContinueThread={
                  typeof onContinueThread === "function"
                    ? onContinueThread
                    : undefined
                }
                scrollToCommentId={scrollToCommentId}
              />
            )}
          </div>
        </div>
  );

  if (variant === "page") {
    return (
      <div
        className={cn(
          "relative flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-background",
          !hidePageHeader && !isMobile && "rounded-lg"
        )}
      >
        {showThreadTitle ? (
          <div className="shrink-0 px-2 pt-2 pb-2 border-b border-border/50 md:px-4">
            <h2 className="text-base font-semibold tracking-tight">Thread</h2>
          </div>
        ) : null}
        {threadBody}
      </div>
    );
  }

  return (
    <Dialog
      open={isOpen}
      modal={false}
      onOpenChange={(next) => {
        if (!next) onClose?.();
      }}
    >
      <DialogContent
        className="max-w-4xl w-full max-h-[90dvh] flex flex-col p-0 overflow-visible shadow-2xl"
        onPointerDownOutside={(e) => {
          if (eventFromDialogSafePortal(e)) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (eventFromDialogSafePortal(e)) e.preventDefault();
        }}
        onFocusOutside={(e) => {
          if (eventFromDialogSafePortal(e)) e.preventDefault();
        }}
      >
        {isMobile ? (
          returnToCatchupHref || onReturnToCatchup ? (
            <div className="shrink-0 flex items-center justify-end gap-2 border-b px-3 py-2 pr-12">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => {
                  if (onReturnToCatchup) {
                    onReturnToCatchup();
                  }
                }}
              >
                <ArrowLeft className="h-4 w-4 mr-1.5" />
                Back to AI summary
              </Button>
            </div>
          ) : (
            <DialogHeader className="sr-only">
              <DialogTitle>{threadTitle}</DialogTitle>
            </DialogHeader>
          )
        ) : (
          <DialogHeader className="px-4 pt-4 pb-3 border-b md:px-6 md:pt-6 md:pb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pr-6">
              <DialogTitle className="text-xl">{threadTitle}</DialogTitle>
              {returnToCatchupHref || onReturnToCatchup ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0 self-start"
                  onClick={() => {
                    if (onReturnToCatchup) {
                      onReturnToCatchup();
                    }
                  }}
                >
                  <ArrowLeft className="h-4 w-4 mr-1.5" />
                  Back to AI summary
                </Button>
              ) : null}
            </div>
          </DialogHeader>
        )}
        {threadBody}
      </DialogContent>
    </Dialog>
  );
};

export default PostThreadView;

